import React from "react";
import PropTypes from "prop-types";
import { SectionCard, ValueText, Lookup } from "frame/components";
import { Typography, Grid, Divider } from "@material-ui/core";
import { formatDate, isNotNullOrEmpty, useTerminology } from "utils";

const ReadOnlyForm = ({ values }) => {
    const { siteLabel } = useTerminology();
    // Helper function to display the correct value based on the field type
    const displayValue = (value) => {
        if (typeof value === 'string') {
            if (value === 'yes') return 'Yes';
            if (value === 'na') return 'NA';
            if (value === '') return 'No';
            return value; // Return any other string value as is
        }
        // Handle boolean values for backward compatibility
        return value ? 'Yes' : 'No';
    };
    
    // Helper function to display completed status correctly
    const displayCompletedValue = (completed) => {
        if (typeof completed === 'boolean') {
            return completed ? "Yes" : "No";
        }
        if (typeof completed === 'string') {
            return (completed === 'true' || completed === 'True') ? "Yes" : "No";
        }
        return "No";
    };
    
    return (
        <>
            <SectionCard>
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label={`${siteLabel} Name`}
                            value={
                                <strong>
                                    <Lookup
                                        id={values.siteId}
                                        resource="sites"
                                        attribute="siteName"
                                    />
                                </strong>
                            }
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Date"
                            value={<strong>{formatDate(values.date)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Inspector"
                            value={<strong>{values.inspector}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Site Control */}
            <SectionCard title="1. Site Control">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Hazard board and signage up-to-date"
                            value={<strong>{displayValue(values.hazardBoardSignage)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Environmental plan – issues"
                            value={<strong>{displayValue(values.environmentalPlanIssues)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Toolbox talk last date"
                            value={<strong>{displayValue(values.toolboxTalk)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Toolbox Talk Date"
                            value={<strong>{formatDate(values.toolboxTalkDate)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Safety inductions for all on site"
                            value={<strong>{displayValue(values.safetyInductions)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Safety notice board current"
                            value={<strong>{displayValue(values.safetyNoticeBoard)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Site Facilities */}
            <SectionCard title="2. Site Facilities">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Offices clean, adequate & good lighting"
                            value={<strong>{displayValue(values.officesClean)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Smoko sheds – clean, potable water"
                            value={<strong>{displayValue(values.smokoShedsClean)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Toilets – clean, washing water"
                            value={<strong>{displayValue(values.toiletsClean)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Tool/equipment sheds adequate"
                            value={<strong>{displayValue(values.toolEquipmentShedsAdequate)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* General Site Tidiness & Access Ways */}
            <SectionCard title="3. General Site Tidiness & Access Ways">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Clear and safe access to the site"
                            value={<strong>{displayValue(values.clearSafeAccess)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Stairways, access ways clear and safe"
                            value={<strong>{displayValue(values.stairwaysAccessWaysClear)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Hoardings and boundary fence secure"
                            value={<strong>{displayValue(values.hoardingsFenceSecure)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Loose materials well secured or protected"
                            value={<strong>{displayValue(values.looseMaterialsSecure)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Personal Safety Equipment */}
            <SectionCard title="4. Personal Safety Equipment">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Signage displayed and legible"
                            value={<strong>{displayValue(values.signageDisplayedLegible)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Hardhats being worn"
                            value={<strong>{displayValue(values.hardhatsWorn)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Correct footwear being worn"
                            value={<strong>{displayValue(values.correctFootwear)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Glasses/ear muffs/vests/masks used"
                            value={<strong>{displayValue(values.glassesEarMuffsVestsMasks)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* First Aid / Fire Prevention */}
            <SectionCard title="5. First Aid / Fire Prevention">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="First aid box available"
                            value={<strong>{displayValue(values.firstAidBox)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Accident register available"
                            value={<strong>{displayValue(values.accidentRegister)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Fire extinguishers available"
                            value={<strong>{displayValue(values.fireExtinguishersAvailable)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Current (12mth)"
                            value={<strong>{displayValue(values.fireExtinguishersCurrent)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Sufficient number"
                            value={<strong>{displayValue(values.fireExtinguishersSufficientNumber)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Evacuation procedure current"
                            value={<strong>{displayValue(values.evacuationProcedureCurrent)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="All emergencies included"
                            value={<strong>{displayValue(values.allEmergenciesIncluded)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Lifting Operations */}
            <SectionCard title="6. Lifting Operations">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Lift Assessment and Plan"
                            value={<strong>{displayValue(values.liftAssessmentPlan)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Crane Certification Current"
                            value={<strong>{displayValue(values.craneCertificationCurrent)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Slings/Chains/Lifting Equipment Certified"
                            value={<strong>{displayValue(values.slingsChainsLifting)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Operator Procedures in Place"
                            value={<strong>{displayValue(values.operatorProcedures)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Inspections Done"
                            value={<strong>{displayValue(values.inspectionsDone)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Man Cage Available"
                            value={<strong>{displayValue(values.manCageAvailable)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Emergency Plan for Lifting"
                            value={<strong>{displayValue(values.emergencyPlanLifting)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Compressed Air */}
            <SectionCard title="7. Compressed Air">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Compressed Air in Good Condition"
                            value={<strong>{displayValue(values.compressedAirCondition)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Guards in Place"
                            value={<strong>{displayValue(values.compressedAirGuards)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Trained Users Only"
                            value={<strong>{displayValue(values.compressedAirTrainedUser)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Excavations / Shoring */}
            <SectionCard title="8. Excavations / Shoring">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Excavations Properly Shored"
                            value={<strong>{displayValue(values.excavationsShored)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Hot Work / Welding */}
            <SectionCard title="9. Hot Work / Welding">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Hot Work Permits"
                            value={<strong>{displayValue(values.hotWorkPermits)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Fire Extinguishers at Hand"
                            value={<strong>{displayValue(values.weldingFireExtinguishers)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Operators Using PPE"
                            value={<strong>{displayValue(values.weldingOperatorsPPE)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Electrical Safety */}
            <SectionCard title="10. Electrical Safety">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Main Board Lockable & Safe"
                            value={<strong>{displayValue(values.mainBoardLockable)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Current Tagged Leads"
                            value={<strong>{displayValue(values.currentTaggedLeads)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Current Tagged Plant"
                            value={<strong>{displayValue(values.currentTaggedPlant)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Current Tagged Lifeguards"
                            value={<strong>{displayValue(values.currentTaggedLifeguards)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Leads Safely Placed"
                            value={<strong>{displayValue(values.leadsSafelyPlaced)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Equipment in Good Condition"
                            value={<strong>{displayValue(values.equipmentGoodCondition)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Appropriate Guards on Equipment"
                            value={<strong>{displayValue(values.appropriateGuardsEquipment)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Adequate Temporary Lighting"
                            value={<strong>{displayValue(values.adequateTemporaryLighting)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Chemical & Hazardous Substances */}
            <SectionCard title="11. Chemical & Hazardous Substances">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Chemicals Correctly Stored"
                            value={<strong>{displayValue(values.chemicalsCorrectlyStored)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="SDS Available"
                            value={<strong>{displayValue(values.sdsAvailable)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Operators Using PPE"
                            value={<strong>{displayValue(values.chemicalOperatorsPPE)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Tools & Task Analysis */}
            <SectionCard title="12. Tools & Task Analysis">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="PAT / TOOL WOF (12mth)"
                            value={<strong>{displayValue(values.patToolWof)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Staff Trained to Use Tools"
                            value={<strong>{displayValue(values.staffTrainedTools)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="PAT Signage & Job Safety Sheets"
                            value={<strong>{displayValue(values.patSignage)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Scaffolding / Work Platforms */}
            <SectionCard title="13. Scaffolding / Work Platforms">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Notifiable / Weekly Inspections Done"
                            value={<strong>{displayValue(values.scaffoldingNotifiableWeekly)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Handrails"
                            value={<strong>{displayValue(values.scaffoldingHandrails)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Toe Boards"
                            value={<strong>{displayValue(values.scaffoldingToeBoards)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Platforms Sound"
                            value={<strong>{displayValue(values.scaffoldingPlatforms)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Ladders Tied & 1m Overhang"
                            value={<strong>{displayValue(values.scaffoldingLadders)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Base Sound and Level"
                            value={<strong>{displayValue(values.scaffoldingBaseSound)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Work Platforms Clear & Safe"
                            value={<strong>{displayValue(values.scaffoldingWorkPlatformsClear)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Platforms Trip Free"
                            value={<strong>{displayValue(values.scaffoldingPlatformsTripFree)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Planks Tied"
                            value={<strong>{displayValue(values.scaffoldingPlanksTied)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Headroom Clear"
                            value={<strong>{displayValue(values.scaffoldingHeadroomClear)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Ties / Bracing"
                            value={<strong>{displayValue(values.scaffoldingTiesBracing)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Ladder Use */}
            <SectionCard title="14. Ladder Use">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Ladders in Good Condition"
                            value={<strong>{displayValue(values.laddersGoodCondition)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Ladders Secured"
                            value={<strong>{displayValue(values.laddersSecured)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Stays"
                            value={<strong>{displayValue(values.laddersStays)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Working 3 Steps Down on Ladder"
                            value={<strong>{displayValue(values.laddersWorkingStepsDown)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Working at Height / Fall Hazards */}
            <SectionCard title="15. Working at Height / Fall Hazards">
                <Grid container spacing={2}>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Floor Edges, Holes, Open Areas Protected"
                            value={<strong>{displayValue(values.fallHazardsFloorEdges)}</strong>}
                        />
                    </Grid>
                    <Grid item sm={6} xs={12}>
                        <ValueText
                            label="Lift Shafts, Pits Protected / Access Controlled"
                            value={<strong>{displayValue(values.fallHazardsLiftShafts)}</strong>}
                        />
                    </Grid>
                </Grid>
            </SectionCard>

            {/* Remedial Actions */}
            {values.remedialActions && values.remedialActions.length > 0 && (
                <SectionCard title="Remedial Actions">
                    {values.remedialActions.map((action, index) => (
                        <React.Fragment key={index}>
                            {index > 0 && <Divider style={{ margin: '16px 0' }} />}
                            <Grid container spacing={2}>
                                <Grid item sm={12} xs={12}>
                                    <Typography variant="h6">Action {index + 1}</Typography>
                                </Grid>
                                <Grid item sm={12} xs={12}>
                                    <ValueText
                                        label="Action"
                                        value={<strong>{action.action || '-'}</strong>}
                                    />
                                </Grid>
                                <Grid item sm={12} xs={12}>
                                    <ValueText
                                        label="Comments"
                                        value={<strong>{action.comments || '-'}</strong>}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <ValueText
                                        label="Person to Action"
                                        value={<strong>{action.person || '-'}</strong>}
                                    />
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <ValueText
                                        label="Completed"
                                        value={<strong>{displayCompletedValue(action.completed)}</strong>}
                                    />
                                </Grid>
                            </Grid>
                        </React.Fragment>
                    ))}
                </SectionCard>
            )}
        </>
    );
};

ReadOnlyForm.propTypes = {
    values: PropTypes.object.isRequired
};

export default ReadOnlyForm; 